import { z } from 'zod';

import { ComponentPayloadValidator } from '../validators/Component';
import { ConverterPayloadValidator } from '../validators/components/Converter';
import { AllPortControlMethods } from '../validators/fields/AbstractPort.ControlMethods';
import { Current } from '../validators/fields/Current';
import { Power } from '../validators/fields/Power';
import { PowerFlowDirection } from '../validators/fields/PowerFlowDirection';
import { Resistance } from '../validators/fields/Resistance';
import { Voltage } from '../validators/fields/Voltage';
import { DiagramPositionSchema } from './DiagramPosition.schema';
import { EdgeSchema } from './Edge.schema';

export const ComponentInstancePortConfiguration = z.preprocess(
    (value) => {
        return value || undefined;
    },
    z
        .object({
            label: z.string().nullable().default(''),
            voltageType: z.enum(['AC', 'DC']).nullable().default(null),
            powerFlowDirection: PowerFlowDirection.validator.nullable().default(null),
            controlMethod: z
                .enum(AllPortControlMethods)
                .nullable()
                .catch(() => null)
                .default(null),
            voltage: Voltage.validators.value,
            current: Current.validators.value,
            power: Power.validators.value,
            droopResistance: Resistance.validators.value,
            droopCurrent: Current.validators.value,
            scale: z.number().nonnegative().nullable().default(null),
            voltageSeries: z
                .object({
                    value: z.array(z.number()).default([]),
                    unit: Voltage.validators.unit,
                })
                .nullable()
                .default({
                    value: [],
                    unit: Voltage.defaultUnit,
                }),
            powerSeries: z
                .object({
                    value: z.array(z.number()).default([]),
                    unit: Power.validators.unit,
                })
                .nullable()
                .default({
                    value: [],
                    unit: Power.defaultUnit,
                }),
            droopCurves: z
                .object({
                    stateOfChargeDerating: z.boolean().nullable().default(null),
                    stateOfChargeSource: z.string().nullable().default(null),
                })
                .nullable()
                .optional(),
        })
        .default({}),
);

export const ComponentInstanceSchema = z.object({
    id: z.string(),
    indicator: z.number(),
    designator: z.string().default(''),
    position: DiagramPositionSchema,
    colSpan: z.number().positive().default(1),
    rowSpan: z.number().positive().default(1),
    componentIcon: z.string().nullable().default(null),
    componentType: ComponentPayloadValidator.shape.type,
    componentId: z.string().nullable().default(null),
    multiple: z
        .object({
            type: z.enum(['parallel', 'series']).default('parallel'),
            amount: z.number().min(1).catch(1).default(1),
            series: z.number().min(1).catch(1).default(1),
        })
        .default({}),
    label: z.string().default(''),
    labelPlacement: z
        .object({
            edge: EdgeSchema.nullable().default(null),
            offset: z.number().gte(0).default(0),
            auto: z.boolean().default(true),
        })
        .default({}),
    notes: z.string().default(''),
    specifications: z.preprocess((value) => {
        return value ? value : undefined;
    }, z.object({}).passthrough().default({})),
    manufacturer: z.string().nullable().default(null), // TODO
    partNumber: z.string().nullable().default(null), // TODO
    configuration: z
        .object({
            ports: z.array(ComponentInstancePortConfiguration).default([]),
            stateOfCharge: z.number().min(0).max(100).nullable().default(null),
        })
        .default({}),
    controller: z
        .object({
            enabled: z.boolean().default(false),
            configuration: z.object({}).passthrough().default({}),
        })
        .default({}),
    liveDataIdentifier: z.string().default(''),
    disabled: z.boolean().default(false),
    createdAt: z.string().optional(),
    createdBy: z.string().optional(),
});

export const ComponentInstanceConverterValidator = ComponentInstanceSchema.extend({
    type: ConverterPayloadValidator.shape.type,
    specifications: ConverterPayloadValidator.pick({
        electrical: true,
    }),
});
